/**
 * P6 — Handoff API: DesignAI → Quote engine.
 * Calls the backend handoff endpoints.
 */
import apiClient from "./client";

export interface HandoffRequest {
  provider: string; // 'tripo3d' | 'meshy' | 'openscad'
  task_id: string;
  printer?: string;
  material?: string;
  infill?: number;
  layer_height?: number;
  supports?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  pincode?: string;
  // P7 height-ask: customer-intended print height (mm). Backend pre-scales
  // the mesh so the verified server-side slice is at the customer's size.
  print_height_mm?: number;
  // designai provenance (09-21): FOF-#### ref → quote.fofus synthesizes the
  // pay rail (order_request + SENT quote) + farm payment gate lookup.
  designai_ref?: string;
  // Print Cart (21 Oct): quote-side cart batch id — the job joins that cart
  // (bulk "Buy all" on quote.fofus.in). Server validates; no money logic here.
  cart_id?: string;
}

export interface HandoffResponse {
  quote_job_id: string | null;
  quote_url: string | null;
  status: string;
  queued: boolean;
}

export interface HandoffStatus {
  status: string;
  attempts: number;
  max_attempts: number;
  quote_job_id: string | null;
  quote_url: string | null;
  last_error: string | null;
  created_at: string | null;
}

/** Send a model to the FOFUS quote engine. */
export async function sendToQuote(req: HandoffRequest): Promise<HandoffResponse> {
  // 16 Oct fleet-truth fix: the farm is 5× Bambu A1 — quote what will actually
  // print (x1c preset doesn't exist on the farm; a1 = `BambuLabA104nozzle`).
  const resp = await apiClient.post<HandoffResponse>(
    "/handoff/to-quote",
    {
      printer: "a1",
      material: "pla",
      infill: 20,
      layer_height: 0.28,
      supports: "auto",
      ...req,
    },
    // 28 Oct: the full chain (provider fetch + pre-scale/decimate + quote
    // forward) regularly exceeds the 30s default — a client timeout here left
    // the panel stuck at 'sending' while the handoff completed server-side.
    { timeout: 120000 },
  );
  return resp.data;
}

/** Check handoff status for a model. */
export async function getHandoffStatus(modelRef: string): Promise<HandoffStatus> {
  const resp = await apiClient.get<HandoffStatus>(
    `/handoff/status/${encodeURIComponent(modelRef)}`
  );
  return resp.data;
}

/** Retry failed handoffs (admin/cron). */
export async function retryFailedHandoffs(): Promise<{
  retry_queue: number;
  entries: string[];
}> {
  const resp = await apiClient.post("/handoff/retry");
  return resp.data;
}

// ── PRINT-STEERING (23 Oct founder): inline REAL price on the result card ──
// After a generation succeeds, the card auto-hands the model to the quote
// engine (silent, cart_id attached) and polls the slice-verified price so the
// customer reads 'For printing: ₹63' RIGHT THERE — no tab juggling, no
// estimate games. The REAL number (server slice) is the trust anchor:
// 'verified here' beats 'instant but ±40% off'.

export interface HandoffPrice {
  state: "none" | "queued" | "pricing" | "priced" | "paid" | "failed";
  amount_inr: number | null;
  min_order_added: boolean;
  quote_job_id: string | null;
  pay_url: string | null;
  quote_url: string | null;
  print_name: string | null;
}

export async function getHandoffPrice(
  provider: string,
  taskId: string
): Promise<HandoffPrice> {
  const resp = await apiClient.get<HandoffPrice>(
    `/handoff/price/${encodeURIComponent(provider)}/${encodeURIComponent(taskId)}`
  );
  return resp.data;
}

/** Fire the silent auto-handoff for a freshly succeeded generation.
 * cart_id rides along so the quote side prices it in the batch lane.
 * Never throws — a failed auto-handoff just means no inline price chip. */
export async function autoHandoffForPrice(req: {
  provider: string;
  task_id: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  designai_ref?: string;
}): Promise<HandoffResponse | null> {
  try {
    const { cartBatchId } = await import("./printCart");
    return await sendToQuote({
      printer: "a1",
      material: "pla",
      infill: 20,
      layer_height: 0.28,
      supports: "auto",
      cart_id: cartBatchId(),
      ...req,
    });
  } catch {
    return null;
  }
}